// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils/classnames';
import { createColumnHelper } from '@tanstack/react-table';
import { Table } from '@shared/ui-kit';
import { type InterfacePositionsAnalysisData } from '@advisor-app/features/positions';
import { type ColumnMethods } from '@advisor-app/features/trades';
import type { TFunction } from 'i18next';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import {
  AppIcon,
  AssetBrand,
  CellData,
  DeleteButton,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';

const columnHelper = createColumnHelper<InterfacePositionsAnalysisData>();

export const renderColumnsTradesSummary = (
  columnMethods: ColumnMethods,
  t: TFunction,
) => {
  const {
    tickerMethod: handleDeleteTradeSummary,
    assetType,
    currency,
    hasOnlyOneData,
  } = columnMethods;

  const isStock = assetType === 'stock';

  const columns = [
    columnHelper.accessor('name', {
      enableHiding: false,
      minSize: 300,
      header: t('common.name', { ns: 'common' }),
      cell: ({ row }) => (
        <AssetBrand
          ticker={row.original.ticker}
          assetType={row.original.asset_type}
          label={row.original.name}
          url={row.original.url_morningstar}
          logoUrl={row.original.url_logo}
          watchlistButton={
            <WatchlistToggleButton
              ticker={row.original.ticker}
              assetType={row.original.asset_type!}
            />
          }
        />
      ),
    }),

    columnHelper.accessor('ticker', {
      cell: ({ getValue }) => getValue(),
      header: t('common.ticker', { ns: 'common' }),
      enableHiding: false,
    }),

    columnHelper.accessor('isin', {
      header: t('common.isin', { ns: 'common' }),
      cell: ({ getValue }) => getValue() ?? '-',
    }),

    columnHelper.accessor('asset_class', {
      header: t('common.asset-class', { ns: 'common' }),
      meta: { initiallyHidden: isStock },
      cell: ({ getValue }) => getValue() ?? '-',
    }),

    columnHelper.accessor('outstanding_balance', {
      header: t('common.investment', { ns: 'common' }),
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <Table.Data
          value={getValue()}
          format="currency"
          formatOptions={{ currency: currency }}
        />
      ),
    }),

    columnHelper.accessor('unrealized_capital_gains', {
      header: t('common.gain-loss', { ns: 'common' }),
      minSize: 150,
      sortingFn: 'gainLoss',
      meta: { alignment: 'end' },
      cell: ({ getValue }) => {
        const [value, percentage] = getValue();
        return (
          <div data-testid="gain-loss" className={value >= 0 ? 'profit' : 'loss'}>
            {formatToMoney(value)} ({percentage}%)
          </div>
        );
      },
    }),

    columnHelper.accessor('ytd_return', {
      header: t('common.ytd-returns', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y1_return', {
      header: t('common.investment-returns-1y', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_return', {
      header: t('common.investment-returns-3y', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y5_return', {
      header: t('common.investment-returns-5y', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y10_return', {
      header: t('common.investment-returns-10y', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_vol', {
      header: t('common.3y-vol', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('max_draw_down', {
      header: t('common.max-drawdown', { ns: 'common' }),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('inception_return', {
      header: t('common.investment-returns-inception', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m1_return', {
      header: t('common.investment-returns-m1', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m3_return', {
      header: t('common.investment-returns-m3', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m6_return', {
      header: t('common.investment-returns-m6', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('price', {
      header: t('common.price', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="currency" />,
    }),

    columnHelper.accessor('price_as_of', {
      header: t('common.updated-on', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="date" />,
    }),

    columnHelper.display({
      id: 'options',
      meta: { showColumnVisibilitySelector: true },
      cell: ({ row }) => {
        const value = row.original.operation;
        return (
          <div className="flex items-center justify-between space-x-4">
            {value === 'BUY' && <AppIcon icon="Buy" />}
            {value === 'SELL' && <AppIcon icon="Sell" />}
            <DeleteButton
              className={classNames({ '!hidden': hasOnlyOneData })}
              data-testid={'delete-trade-' + row.original.ticker}
              onClick={() =>
                handleDeleteTradeSummary &&
                handleDeleteTradeSummary(row.original.ticker)
              }
            />
          </div>
        );
      },
    }),
  ];

  return columns;
};
